import { FILE_UPLOAD_ENDPOINTS } from "@endpoints/fileUpload";
import { saveUploadedAttachment } from "@hooks/useFileUpload";
import Spinner from "@legacy/core/components/Spinner";
import pluralize from "pluralize";

import { PERMISSION_LEVEL } from "@constants/permissionLevel";
import { validateLineItem } from "@legacy/invoices/utils/utils";
import { roundHalfUp } from "@utils/roundHalfUp";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import { Component } from "react";
import deepcopy from "rfdc";
import LineItemForm from "../core/forms/LineItemForm";
import { CustomIDGenerationModes, LineItemUnitTypes, PriceBookItemTypes } from "../core/utils/enums";
import { currencyFormatter, fetchIntersectedLabels, getCurrencySymbol, historyHasState, sendDataToServer, valueIsDefined } from "../core/utils/utils";
import VendorForm from "../pricebook/forms/VendorForm";
import VendorCreditForm from "./forms/VendorCreditForm";

dayjs.extend(timezone)

const FORM_MODES = {
    ADD_VENDOR_CREDIT: "ADD_VENDOR_CREDIT",
    EDIT_VENDOR_CREDIT: "EDIT_VENDOR_CREDIT",
    ADD_VENDOR: "ADD_VENDOR",
    EDIT_VENDOR: "EDIT_VENDOR",
    ADD_SERVICE_CHARGE: "ADD_SERVICE_CHARGE",
    EDIT_SERVICE_CHARGE: "EDIT_SERVICE_CHARGE",
    ADD_PART: "ADD_PART",
    EDIT_PART: "EDIT_PART",
    ADD_OTHER_CHARGE: "ADD_OTHER_CHARGE",
    EDIT_OTHER_CHARGE: "EDIT_OTHER_CHARGE",
}

const FORM_MODE_SUBTITLES = {
    ADD_VENDOR_CREDIT: "Add Vendor Credit Details",
    EDIT_VENDOR_CREDIT: "Edit Vendor Credit Details",
    ADD_VENDOR: "New Vendor",
    EDIT_VENDOR: "Edit Vendor",
    ADD_SERVICE_CHARGE: "Add Service Charge",
    EDIT_SERVICE_CHARGE: "Edit Service Charge",
    ADD_PART: "Add Part or Material",
    EDIT_PART: "Edit Part or Material",
    ADD_OTHER_CHARGE: "Add Miscellaneous Charge",
    EDIT_OTHER_CHARGE: "Edit Miscellaneous Charge",
}

const FORM_MODE_BACK_BUTTON_DISPLAY = {
    ADD_VENDOR_CREDIT: "flex",
    EDIT_VENDOR_CREDIT: "flex",
    ADD_VENDOR: "none",
    EDIT_VENDOR: "none",
    ADD_SERVICE_CHARGE: "none",
    EDIT_SERVICE_CHARGE: "none",
    ADD_PART: "none",
    EDIT_PART: "none",
    ADD_OTHER_CHARGE: "none",
    EDIT_OTHER_CHARGE: "none",
}

const PRIMARY_FORM_MODES = [FORM_MODES.ADD_VENDOR_CREDIT, FORM_MODES.EDIT_VENDOR_CREDIT]

const VENDOR_FORM_MODES = [
    FORM_MODES.ADD_VENDOR,
    FORM_MODES.EDIT_VENDOR,
]

const LINE_ITEM_FORM_MODES = [
    FORM_MODES.ADD_SERVICE_CHARGE,
    FORM_MODES.EDIT_SERVICE_CHARGE,
    FORM_MODES.ADD_PART,
    FORM_MODES.EDIT_PART,
    FORM_MODES.ADD_OTHER_CHARGE,
    FORM_MODES.EDIT_OTHER_CHARGE,
]

const SECONDARY_FORM_MODES = [...VENDOR_FORM_MODES, ...LINE_ITEM_FORM_MODES]

const FORM_DATA_NAMES_BY_MODE = {
    ADD_VENDOR_CREDIT: "vendorCreditData",
    EDIT_VENDOR_CREDIT: "vendorCreditData",
    ADD_VENDOR: "vendorData",
    EDIT_VENDOR: "vendorData",
    ADD_SERVICE_CHARGE: "lineItemData",
    EDIT_SERVICE_CHARGE: "lineItemData",
    ADD_PART: "lineItemData",
    EDIT_PART: "lineItemData",
    ADD_OTHER_CHARGE: "lineItemData",
    EDIT_OTHER_CHARGE: "lineItemData",
}

const SUBMITTING_NAMES_BY_MODE = {
    ADD_VENDOR_CREDIT: "submittingVendorCredit",
    EDIT_VENDOR_CREDIT: "submittingVendorCredit",
    ADD_VENDOR: "submittingVendor",
    EDIT_VENDOR: "submittingVendor",
    ADD_SERVICE_CHARGE: "submittingLineItem",
    EDIT_SERVICE_CHARGE: "submittingLineItem",
    ADD_PART: "submittingLineItem",
    EDIT_PART: "submittingLineItem",
    ADD_OTHER_CHARGE: "submittingLineItem",
    EDIT_OTHER_CHARGE: "submittingLineItem",
}

const ERROR_NAMES_BY_MODE = {
    ADD_VENDOR_CREDIT: "vendorCredit",
    EDIT_VENDOR_CREDIT: "vendorCredit",
    ADD_VENDOR: "vendor",
    EDIT_VENDOR: "vendor",
    ADD_SERVICE_CHARGE: "lineItem",
    EDIT_SERVICE_CHARGE: "lineItem",
    ADD_PART: "lineItem",
    EDIT_PART: "lineItem",
    ADD_OTHER_CHARGE: "lineItem",
    EDIT_OTHER_CHARGE: "lineItem",
}

const LINE_ITEM_TYPES = {
    SERVICE_CHARGES: "service_charges",
    PARTS: "parts",
    OTHER_CHARGES: "other_charges",
}

const LINE_ITEM_TYPE_MAP = {
    [LINE_ITEM_TYPES.SERVICE_CHARGES]: PriceBookItemTypes.service,
    [LINE_ITEM_TYPES.PARTS]: PriceBookItemTypes.part,
    [LINE_ITEM_TYPES.OTHER_CHARGES]: PriceBookItemTypes.other,
}

const LINE_ITEM_TYPE_BY_MODE = {
    ADD_SERVICE_CHARGE: PriceBookItemTypes.service,
    EDIT_SERVICE_CHARGE: PriceBookItemTypes.service,
    ADD_PART: PriceBookItemTypes.part,
    EDIT_PART: PriceBookItemTypes.part,
    ADD_OTHER_CHARGE: PriceBookItemTypes.other,
    EDIT_OTHER_CHARGE: PriceBookItemTypes.other,
}


class VendorCreditCreateContainer extends Component {

    // Initialize

    constructor(props) {
        super(props)

        const defaultMode = this.props.formMode || FORM_MODES.ADD_VENDOR_CREDIT
        this.addToastToQueue = this.props.addToastToQueue

        this.state = {
            vendorCreditData: null,
            billData: null,
            vendorData: {},
            lineItemData: {},

            pendingSessionId: crypto.randomUUID(),
            attachments: [],
            isUploading: false,

            selectedVendor: null,

            isDraft: false,

            errors: {
                vendorCredit: {},
                vendor: {},
                lineItem: {},
            },

            defaultMode: defaultMode,
            mode: defaultMode,

            preferredTimezone: window.PREFERRED_TIMEZONE,
            currencyCode: window.CURRENCY_CODE,
            languageCode: window.LANGUAGE_CODE,

            showCustomVendorCreditIDField: window.VENDOR_CREDIT_CUSTOM_ID_GENERATION_MODE === CustomIDGenerationModes.manual,

            returnScroll: 0,
        }

        window.onpopstate = (event) => {
            if (event.state !== null && Object.keys(event.state).length) {
                this.setState(event.state)
            }
        }
    }

    componentDidMount = async () => {
        // If props tell us this is an edit view, grab vendor credit data
        if (this.state.defaultMode === FORM_MODES.EDIT_VENDOR_CREDIT && window.VENDOR_CREDIT_ID) {
            if (this.state.vendorCreditData === null) {
                const vendorCreditEndpoint = DjangoUrls["bills:api-vendor-credits-detail"](window.MARKETPLACE_ENTITY_SLUG, window.VENDOR_CREDIT_ID)
                const vendorCreditResponse = await fetch(vendorCreditEndpoint)
                const vendorCredit = await vendorCreditResponse.json()

                this.setState((state, props) => {
                    let updatedState = state
                    updatedState.vendorCreditData = vendorCredit

                    updatedState.selectedVendor = vendorCredit.vendor

                    updatedState.isDraft = vendorCredit.is_draft

                    updatedState.attachments = deepcopy()(vendorCredit.attachments)
                    updatedState.labels = deepcopy()(vendorCredit.labels)

                    // Set bill link if present
                    if (vendorCredit.bill !== null && vendorCredit.bill !== undefined) {
                        updatedState.billData = deepcopy()(vendorCredit.bill)

                        updatedState.vendorCreditData.bill = vendorCredit.bill.id
                    }
                    else {
                        updatedState.billData = {}
                    }

                    // Convert service company to slug
                    updatedState.vendorCreditData.service_company = vendorCredit.service_company.slug

                    // Convert vendor to ID
                    updatedState.vendorCreditData.vendor = vendorCredit.vendor.id

                    this.resetVendorCreditData(updatedState, state, vendorCredit)

                    vendorCredit.line_items.forEach(lineItem => { lineItem.rowKey = crypto.randomUUID() })

                    // Split line items by type
                    updatedState.vendorCreditData.service_charges = vendorCredit.line_items.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.service)
                    updatedState.vendorCreditData.parts = vendorCredit.line_items.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.part)
                    updatedState.vendorCreditData.other_charges = vendorCredit.line_items.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.other)

                    return updatedState
                })
            }
        }
        else {
            const localTime = dayjs.tz(undefined, window.PREFERRED_TIMEZONE)
            const localDateString = localTime.format("YYYY-MM-DD")

            let bill = null

            // Check for from_bill URL parameter
            const hasBillsEntitlement = !!window.CURRENT_USER?.service_company?.entitlement_bills_enabled
            const hasBillsPermissions = window.CURRENT_USER?.permissions.bills_view_permission >= PERMISSION_LEVEL.FULL
            const fromBillID = new URLSearchParams(document.location.search).get("from_bill") || null

            // The bill's line picker names the chosen lines as lines=<bill line id>,...
            const linesParam = new URLSearchParams(document.location.search).get("lines")
            const chosenBillLineItemIDs = linesParam !== null ? new Set(linesParam.split(",").filter(Boolean)) : null

            if (hasBillsEntitlement && hasBillsPermissions && fromBillID !== null && this.state.billData === null) {
                const billEndpoint = DjangoUrls["bills:api-bills-detail"](window.MARKETPLACE_ENTITY_SLUG, fromBillID)

                const billResponse = await fetch(billEndpoint)
                bill = await billResponse.json()

                this.setState((state, props) => {
                    let updatedState = state
                    updatedState.billData = bill
                    return updatedState
                })
            }
            else {
                this.setState((state, props) => {
                    let updatedState = state
                    updatedState.billData = {}
                    return updatedState
                })
            }

            this.setState((state, props) => {
                let updatedState = state

                this.resetVendorCreditData(updatedState, state, {})

                updatedState.vendorCreditData.service_company = window.MARKETPLACE_ENTITY_SLUG
                updatedState.vendorCreditData.date_issued = localDateString
                updatedState.vendorCreditData.attachments = []
                updatedState.vendorCreditData.labels = []
                updatedState.vendorCreditData.service_charges = []
                updatedState.vendorCreditData.parts = []
                updatedState.vendorCreditData.other_charges = []

                // Set fields from the bill (if from a bill)
                if (Object.keys(state.billData).length !== 0) {
                    updatedState.vendorCreditData.bill = state.billData.id

                    // Pull the vendor from the bill
                    updatedState.selectedVendor = state.billData.vendor
                    updatedState.vendorCreditData.vendor = updatedState.selectedVendor.id

                    // Pre-populate VC line items from bill line items. Re-home the source
                    // bill line item id and reset the row's own id to null so each row saves
                    // as a new line (matching every other conversion flow). A vendor credit carries
                    // no discounts or service dates. Copy the chosen bill lines, or every line
                    // other credits haven't used up, at the quantity left on each.
                    const billLineItems = (state.billData.line_items || []).filter(lineItem => lineItem.line_item_type !== PriceBookItemTypes.discount)
                    const vendorCreditLineItems = billLineItems
                        .map(lineItem => {
                            const creditedQuantity = lineItem.vendor_credits.reduce((total, vendorCredit) => roundHalfUp(total + parseFloat(vendorCredit.quantity), 3), 0)
                            const creditedAmount = lineItem.vendor_credits.reduce((total, vendorCredit) => roundHalfUp(total + parseFloat(vendorCredit.amount), 2), 0)
                            const lineQuantity = parseFloat(lineItem.quantity)
                            const lineAmount = roundHalfUp(lineQuantity * parseFloat(lineItem.price), 2)

                            return {
                                lineItem,
                                quantityLeft: roundHalfUp(lineQuantity - creditedQuantity, 3),
                                isFullyCredited: creditedQuantity >= lineQuantity || creditedAmount >= lineAmount,
                            }
                        })
                        .filter(({ lineItem, isFullyCredited }) => (
                            chosenBillLineItemIDs === null ? !isFullyCredited : chosenBillLineItemIDs.has(String(lineItem.id))
                        ))
                        .map(({ lineItem: { id, vendor_credits, service_start_date, service_end_date, ...lineItem }, quantityLeft }) => ({
                            ...lineItem,
                            id: null,
                            rowKey: crypto.randomUUID(),
                            quantity: quantityLeft.toFixed(3),
                            bill_line_item: id,
                            remove_from_inventory: false,
                            inventory_location: null,
                        }))

                    updatedState.vendorCreditData.service_charges = vendorCreditLineItems.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.service)
                    updatedState.vendorCreditData.parts = vendorCreditLineItems.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.part)
                    updatedState.vendorCreditData.other_charges = vendorCreditLineItems.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.other)
                }

                this.resetVendorCreditData(updatedState, state, updatedState.vendorCreditData)

                return updatedState
            })

            // Bring over labels from the bill that also exist on vendor credits
            if (bill?.labels?.length > 0) {
                const vendorCreditLabelsEndpoint = DjangoUrls["bills:api-vendor-credits-labels-list"](window.MARKETPLACE_ENTITY_SLUG)
                const intersectedLabels = await fetchIntersectedLabels(bill.labels, vendorCreditLabelsEndpoint)
                this.setState((state) => {
                    let updatedState = state
                    updatedState.vendorCreditData.labels = deepcopy()(intersectedLabels)
                    return updatedState
                })
            }
        }

        if (historyHasState(history)) {
            document.querySelector(".page-subtitle").innerHTML = FORM_MODE_SUBTITLES[history.state.mode]
            document.querySelector(".back-button").style.display = FORM_MODE_BACK_BUTTON_DISPLAY[history.state.mode]
            this.setState(history.state)
        }
    }

    // Helpers

    getBillLineItemChoices = () => {
        // A bill line another credit already holds names that credit; the server caps what the line can give
        const billLineItems = this.state.billData?.line_items || []

        return billLineItems
            .filter(billLineItem => billLineItem.line_item_type !== PriceBookItemTypes.discount)
            .map(billLineItem => {
                const otherVendorCreditIDs = (billLineItem.vendor_credits || [])
                    .filter(vendorCredit => vendorCredit.id !== this.state.vendorCreditData.id)
                    .map(vendorCredit => vendorCredit.custom_id || vendorCredit.id)

                return otherVendorCreditIDs.length === 0
                    ? billLineItem
                    : {...billLineItem, __str__: `${billLineItem.__str__} (on ${otherVendorCreditIDs.join(", ")})`}
            })
    }

    // Form helpers

    updateFormData = (formName, fieldName, fieldValue) => {
        this.setState((state, props) => {
            let updatedState = state
            updatedState[formName][fieldName] = fieldValue
            return updatedState
        })
    }

    switchFormMode = (mode) => {
        document.querySelector(".page-subtitle").innerHTML = FORM_MODE_SUBTITLES[mode]
        document.querySelector(".back-button").style.display = FORM_MODE_BACK_BUTTON_DISPLAY[mode]

        if (SECONDARY_FORM_MODES.includes(mode)) {
            history.replaceState(this.state, "", "")
        }

        this.setState((state, props) => {
            let updatedState = state
            updatedState.mode = mode
            history.pushState(updatedState, "", "?mode=" + mode.toLowerCase().replace(/_/g, "-"));
            return updatedState
        })
    }

    switchToPrimaryForm = () => {
        this.setState((state, props) => {
            let updatedState = state

            // Clear the secondary form data
            updatedState[FORM_DATA_NAMES_BY_MODE[state.mode]] = {}
            updatedState[SUBMITTING_NAMES_BY_MODE[state.mode]] = false
            updatedState.errors[ERROR_NAMES_BY_MODE[state.mode]] = {}

            return updatedState
        })
        this.switchFormMode(this.state.defaultMode)
    }

    switchToSecondaryForm = (newFormMode, data, initialData) => {
        this.setState((state, props) => {
            let updatedState = state
            // Set the scroll state
            updatedState.returnScroll = document.querySelector(".main").scrollTop

            updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]] = {}

            if (data !== null) {
                updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]] = deepcopy()(data)
            }
            else {
                if (LINE_ITEM_FORM_MODES.includes(newFormMode)) {
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].line_item_type = LINE_ITEM_TYPE_BY_MODE[newFormMode]
                }
            }

            if (initialData !== null) {
                Object.assign(updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]], initialData)
            }

            return updatedState
        })

        this.switchFormMode(newFormMode)
    }

    resetVendorCreditData = (updatedState, state, vendorCreditData) => {
        updatedState.vendorCreditData = vendorCreditData
        updatedState.vendorCreditData.is_draft = state.isDraft
    }

    updateVendorSelection = (selectedVendor) => {
        this.setState((state, props) => {
            let updatedState = state

            if (selectedVendor !== null) {
                updatedState.selectedVendor = selectedVendor
                updatedState.vendorCreditData.vendor = selectedVendor.id

                this.resetVendorCreditData(updatedState, state, state.vendorCreditData)
            }
            else {
                updatedState.selectedVendor = null
            }

            return updatedState
        })
    }

    updateAttachments = (attachmentUploadData) => {
        this.setState((state, props) => {
            let updatedState = state

            // React can run this updater twice, so an upload already in the list isn't added again
            const listedFilePaths = new Set(updatedState.attachments.map(attachment => attachment.filePath))
            const newAttachments = attachmentUploadData.filter(attachment => !listedFilePaths.has(attachment.filePath))

            updatedState.attachments = [...updatedState.attachments, ...newAttachments]
            updatedState.vendorCreditData.attachments = [...updatedState.vendorCreditData.attachments, ...newAttachments]

            return updatedState
        })
    }

    removePendingAttachment = (filenameOrId) => {
        this.setState((state, props) => {
            let updatedState = state

            // Remove pending attachments by filename OR existing attachments by ID
            const filterFn = (attachment) => {
                if (attachment.isPending && attachment.upload_filename === filenameOrId) return false
                if (attachment.id && String(attachment.id) === String(filenameOrId)) return false
                return true
            }

            updatedState.attachments = updatedState.attachments.filter(filterFn)
            updatedState.vendorCreditData.attachments = updatedState.vendorCreditData.attachments.filter(filterFn)

            return updatedState
        })
    }

    setIsUploading = (isUploading) => {
        this.setState({ isUploading })
    }

    // Crud Line Items

    addCurrentLineItem = (lineItemListName) => {
        const {isValid, errors} = validateLineItem(this.state.lineItemData, true, false, false, false, true, false)

        if (isValid) {
            this.setState((state, props) => {
                let updatedState = state

                let data = state.lineItemData
                data.line_item_type = LINE_ITEM_TYPE_MAP[lineItemListName]

                data.id = null
                data.rowKey = crypto.randomUUID()

                if (!valueIsDefined(data.price)) {
                    data.price = null
                }

                if (data.line_item_type === PriceBookItemTypes.service) {
                    if (!valueIsDefined(data.unit_type)) {
                        data.unit_type = LineItemUnitTypes.hourly
                    }

                    if (data.unit_type === LineItemUnitTypes.flat_rate) {
                        data.quantity = 1
                    }
                }
                else {
                    delete data.unit_type
                }

                updatedState.vendorCreditData[lineItemListName].push(data)
                updatedState.errors.lineItem = {}
                return updatedState
            })
            this.switchToPrimaryForm()
        }
        else {
            this.setState((state, props) => {
                let updatedState = state
                updatedState.errors.lineItem = errors
                return updatedState
            })
        }
    }

    lineItemEquals = (existing, updated) => {
        // A line that isn't saved yet has no ID, so lines are told apart by a key of their own
        return existing.rowKey === updated.rowKey
    }

    updateCurrentLineItem = (lineItemListName) => {
        const {isValid, errors} = validateLineItem(this.state.lineItemData, true, false, false, false, true, false)

        if (isValid) {
            this.setState((state, props) => {
                let updatedState = state

                let data = state.lineItemData

                if (!valueIsDefined(data.price)) {
                    data.price = null
                }

                if (data.line_item_type === PriceBookItemTypes.service) {
                    if (!valueIsDefined(data.unit_type)) {
                        data.unit_type = LineItemUnitTypes.hourly
                    }

                    if (data.unit_type === LineItemUnitTypes.flat_rate) {
                        data.quantity = 1
                    }
                }
                else {
                    delete data.unit_type
                }

                const lineItemIndex = updatedState.vendorCreditData[lineItemListName].findIndex(lineItem => this.lineItemEquals(lineItem, state.lineItemData))

                updatedState.vendorCreditData[lineItemListName][lineItemIndex] = data
                updatedState.errors.lineItem = {}
                return updatedState
            })
            this.switchToPrimaryForm()
        }
        else {
            this.setState((state, props) => {
                let updatedState = state
                updatedState.errors.lineItem = errors
                return updatedState
            })
        }
    }

    deleteCurrentLineItem = (lineItemListName) => {
        this.setState((state, props) => {
            let updatedState = state

            const data = state.lineItemData

            updatedState.vendorCreditData[lineItemListName] = updatedState.vendorCreditData[lineItemListName].filter(lineItem => !this.lineItemEquals(lineItem, data))
            updatedState.errors.lineItem = {}
            return updatedState
        })
        this.switchToPrimaryForm()
    }

    // Crud Vendor Credit

    previewVendorCredit = async () => {
        // Create a draft and redirect to the preview page
        const endpoint = DjangoUrls["bills:api-vendor-credits-list"](window.MARKETPLACE_ENTITY_SLUG)

        const onSuccess = (vendorCredit) => {
            const successUrl = DjangoUrls["bills:vendor-credits-detail"](window.MARKETPLACE_ENTITY_SLUG, vendorCredit.id)
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Vendor Credit "${vendorCredit.custom_id || vendorCredit.id}" draft saved`,
                path: successUrl,
                delayRender: true,
            })
            history.replaceState({}, "", "")
            location.assign(successUrl)
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Vendor Credit draft could not be saved",
            })
        }

        this.CDVendorCredit(endpoint, "POST", onSuccess, onError, true)
    }


    createVendorCredit = async (isDraft) => {
        const endpoint = DjangoUrls["bills:api-vendor-credits-list"](window.MARKETPLACE_ENTITY_SLUG)

        const onSuccess = async (vendorCredit) => {
            // Create attachment records for pending uploads
            const pendingAttachments = this.state.attachments.filter(attachment => attachment.isPending)
            if (pendingAttachments.length > 0) {
                const attachmentsEndpoint = DjangoUrls["bills:api-vendor-credits-attachments-list"](window.MARKETPLACE_ENTITY_SLUG, vendorCredit.id)

                let failedAttachmentCount = 0

                for (const attachment of pendingAttachments) {
                    try {
                        await saveUploadedAttachment({
                            attachmentsEndpoint,
                            uploadConfigEndpoint: `${FILE_UPLOAD_ENDPOINTS.PENDING_UPLOAD_CONFIG}?session_id=${attachment.sessionId}`,
                            file: attachment.file,
                            filePath: attachment.filePath,
                        })
                    } catch (error) {
                        failedAttachmentCount++
                        console.error('Failed to create attachment record:', error)
                    }
                }

                if (failedAttachmentCount > 0) {
                    this.addToastToQueue({
                        type: "error",
                        variant: "default",
                        title: `${failedAttachmentCount} ${pluralize("attachment", failedAttachmentCount)} failed to save`,
                    })
                }
            }

            let successUrl

            if (window.CURRENT_USER?.permissions.bills_list_permission < PERMISSION_LEVEL.FULL) {
                successUrl = DjangoUrls["dashboard:dashboard"](window.MARKETPLACE_ENTITY_SLUG)
            } else if (isDraft || window.CURRENT_USER?.permissions.bills_view_permission < PERMISSION_LEVEL.FULL) {
                successUrl = DjangoUrls["bills:vendor-credits-list"](window.MARKETPLACE_ENTITY_SLUG)
            }
            else {
                successUrl = DjangoUrls["bills:vendor-credits-detail"](window.MARKETPLACE_ENTITY_SLUG, vendorCredit.id)
            }
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Vendor Credit "${vendorCredit.custom_id || vendorCredit.id}" ${isDraft ? "draft saved" : "created"}`,
                cta:
                    isDraft && window.CURRENT_USER?.permissions.bills_view_permission >= PERMISSION_LEVEL.FULL ?
                    {
                        children: "View",
                        destination: DjangoUrls["bills:vendor-credits-detail"](window.MARKETPLACE_ENTITY_SLUG, vendorCredit.id)
                    }
                    :
                    undefined,
                path: successUrl.split("?")[0],
                delayRender: true,
            })

            history.replaceState({}, "", "")
            location.assign(successUrl)
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: `Vendor Credit ${isDraft ? "draft could not be saved" : "could not be created"}`,
            })
        }

        this.CDVendorCredit(endpoint, "POST", onSuccess, onError, isDraft)
    }

    deleteDraftVendorCredit = async () => {
        const endpoint = DjangoUrls["bills:api-vendor-credits-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.vendorCreditData.id)
        let successUrl = DjangoUrls["bills:vendor-credits-list"](window.MARKETPLACE_ENTITY_SLUG)

        if (valueIsDefined(this.state.vendorCreditData.bill)) {
            successUrl = DjangoUrls["bills:bills-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.vendorCreditData.bill)
        }

        const onSuccess = () => {
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Vendor Credit "${this.state.vendorCreditData.custom_id || this.state.vendorCreditData.id}" draft deleted`,
                path: successUrl.split("?")[0],
                delayRender: true,
            })
            history.replaceState({}, "", "")
            location.assign(successUrl)
        }

        document.querySelectorAll("#message_modal_confirm_delete_draft .modal__close .button").forEach(button => button.style.display = "none")
        document.querySelector("#message_modal_confirm_delete_draft .modal__close .spinner-centered").style.display = "block"

        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Vendor Credit draft could not be deleted",
            })
            const deleteDraftModal = document.querySelector("#message_modal_confirm_delete_draft .modal__close")
            if (deleteDraftModal) {
                deleteDraftModal.innerHTML = '<span class="text-invalid"><strong>An unexpected error occurred.</strong></span>'
            }
        }

        this.CDVendorCredit(endpoint, "DELETE", onSuccess, onError, false)
    }

    CDVendorCredit = async (endpoint, endpointMethod, onSuccess, onError, isDraft) => {
        const dataName = "vendorCreditData"
        const submittingName = "submittingVendorCredit"
        const errorDictName = "vendorCredit"

        const dataManipulator = (data, state) => {
            // Deepcopy first to avoid mutating state — without this,
            // destructive operations (like deleting attachments) corrupt
            // state and clear UI data when the server returns an error.
            const cleanData = deepcopy()(data)

            // The row key only tells lines apart in the form
            for (const lineItem of [...cleanData.service_charges, ...cleanData.parts, ...cleanData.other_charges]) {
                delete lineItem.rowKey
            }

            cleanData.line_items = [
                ...cleanData.service_charges,
                ...cleanData.parts,
                ...cleanData.other_charges,
            ]

            // Convert blank custom ID value to null
            cleanData.custom_id = cleanData.custom_id || null

            // Set a blank label list if there aren't any selected
            cleanData.labels = cleanData.labels || []

            // For existing objects with file uploads, attachments are managed by the
            // FileUpload component via the attachment API — don't send them in the
            // form payload or the nested serializer will overwrite them.
            // For new objects, filter out pending attachments — they're created after
            // the object exists.
            if (cleanData.id && window.FILE_UPLOADS_ENABLED) {
                delete cleanData.attachments
            } else if (cleanData.attachments) {
                cleanData.attachments = cleanData.attachments.filter(attachment => !attachment.isPending && attachment.id)
            }

            cleanData.is_draft = isDraft  // We don't want this to change the actual object
            return cleanData
        }

        const setErrors = (fieldName, message, errorDict) => {
            if (fieldName === "non_field_errors" && message === "The fields service_company, custom_id must make a unique set.") {
                errorDict["custom_id"] = "A vendor credit with this ID already exists."
            }
            else if (fieldName === "line_items") {
                if (typeof message === "string") {
                    errorDict["line_items"] = message
                } else {
                    errorDict["line_items"] = "Please correct the line item errors below:"

                    // Apply the nested errors
                    this.setState((state, props) => {
                        let updatedState = state

                        const indexedLineItems = [
                            ...updatedState[FORM_DATA_NAMES_BY_MODE[state.mode]][LINE_ITEM_TYPES.SERVICE_CHARGES],
                            ...updatedState[FORM_DATA_NAMES_BY_MODE[state.mode]][LINE_ITEM_TYPES.PARTS],
                            ...updatedState[FORM_DATA_NAMES_BY_MODE[state.mode]][LINE_ITEM_TYPES.OTHER_CHARGES],
                        ]
                        message.map((lineItemError, index) => indexedLineItems[index].errors = lineItemError)

                        return updatedState
                    })
                }
            }
            else if (fieldName === "labels" && Array.isArray(message)) {
                errorDict["labels"] = "Labels must be fewer than 100 characters."
            }
            else {
                errorDict[fieldName] = message
            }
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, dataManipulator, setErrors)
    }

    // Crud Vendor

    createVendor = async () => {
        const endpoint = DjangoUrls["pricebook:api-vendors-list"](window.MARKETPLACE_ENTITY_SLUG)
        const endpointMethod = "POST"

        const onSuccess = (vendor) => {
            this.updateVendorSelection(vendor)
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Vendor "${vendor.name}" created`,
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Vendor could not be created",
            })
        }

        this.createUpdateVendor(endpoint, endpointMethod, onSuccess, onError)
    }

    updateVendor = async () => {
        const endpoint = DjangoUrls["pricebook:api-vendors-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.selectedVendor.id)
        const endpointMethod = "PUT"

        const onSuccess = (vendor) => {
            this.updateVendorSelection(vendor)
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Vendor "${vendor.name}" updated`,
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Vendor could not be updated",
            })
        }

        this.createUpdateVendor(endpoint, endpointMethod, onSuccess, onError)
    }

    createUpdateVendor = async (endpoint, endpointMethod, onSuccess, onError) => {
        const dataName = "vendorData"
        const submittingName = "submittingVendor"
        const errorDictName = "vendor"

        const dataManipulator = (data) => {
            data.contacts = data.contacts || []
            return data
        }

        const setErrors = (fieldName, message, errorDict) => {
            if (fieldName === "non_field_errors" && message === "The fields service_company, name must make a unique set.") {
                errorDict["name"] = "A vendor with this name already exists."
            }
            else if (fieldName === "contacts") {
                errorDict["contacts"] = "Please correct the contact errors below:"

                this.setState((state, props) => {
                    let updatedState = state
                    message.map((contactError, index) => updatedState[FORM_DATA_NAMES_BY_MODE[state.mode]].contacts[index].errors = contactError)
                    return updatedState
                })
            }
            else {
                errorDict[fieldName] = message
            }
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, dataManipulator, setErrors)
    }

    // Handle Actions

    handleActionRequest = (action) => {
        switch (action) {
            case "VENDOR_CREDIT_PREVIEW":
                this.previewVendorCredit()
                break
            case "VENDOR_CREDIT_CREATE":
                this.createVendorCredit(false)
                break
            case "VENDOR_CREDIT_CREATE_DRAFT":
                this.createVendorCredit(true)
                break
            case "VENDOR_CREDIT_DELETE_DRAFT":
                window.deleteDraftVendorCredit = this.deleteDraftVendorCredit
                document.querySelector("#message_modal_confirm_delete_draft").style.display = ""
                window.MicroModal.show("message_modal_confirm_delete_draft")
                break
            case "VENDOR_CREATE":
                this.createVendor()
                break
            case "VENDOR_UPDATE":
                this.updateVendor()
                break
            case "VENDOR_CANCEL_CREATE":
                this.switchToPrimaryForm()
                break
            case "VENDOR_CANCEL_EDITS":
                this.switchToPrimaryForm()
                break
            case "ADD_SERVICE_CHARGE":
                this.addCurrentLineItem(LINE_ITEM_TYPES.SERVICE_CHARGES)
                break
            case "EDIT_SERVICE_CHARGE":
                this.updateCurrentLineItem(LINE_ITEM_TYPES.SERVICE_CHARGES)
                break
            case "DELETE_SERVICE_CHARGE":
                this.deleteCurrentLineItem(LINE_ITEM_TYPES.SERVICE_CHARGES)
                break
            case "ADD_PART":
                this.addCurrentLineItem(LINE_ITEM_TYPES.PARTS)
                break
            case "EDIT_PART":
                this.updateCurrentLineItem(LINE_ITEM_TYPES.PARTS)
                break
            case "DELETE_PART":
                this.deleteCurrentLineItem(LINE_ITEM_TYPES.PARTS)
                break
            case "ADD_OTHER_CHARGE":
                this.addCurrentLineItem(LINE_ITEM_TYPES.OTHER_CHARGES)
                break
            case "EDIT_OTHER_CHARGE":
                this.updateCurrentLineItem(LINE_ITEM_TYPES.OTHER_CHARGES)
                break
            case "DELETE_OTHER_CHARGE":
                this.deleteCurrentLineItem(LINE_ITEM_TYPES.OTHER_CHARGES)
                break
            default:
                console.error(`No action handler exists for action "${action}".`)
        }
    }

    // Render

    render() {
        if (this.state.vendorCreditData === null || this.state.billData === null) {
            return <Spinner centered={true} />
        }
        else {
            if (PRIMARY_FORM_MODES.includes(this.state.mode)) {
                return <VendorCreditForm
                    mode={this.state.mode}
                    submitting={this.state.submittingVendorCredit}
                    vendorCredit={this.state.vendorCreditData}
                    bill={this.state.billData}
                    billLineItemChoices={this.getBillLineItemChoices()}
                    errors={this.state.errors.vendorCredit}
                    onFormDataChange={(fieldName, fieldValue) => this.updateFormData("vendorCreditData", fieldName, fieldValue)}
                    requestAction={this.handleActionRequest}
                    switchToSecondaryForm={this.switchToSecondaryForm}
                    updateVendorSelection={this.updateVendorSelection}
                    selectedVendor={this.state.selectedVendor}
                    formatCurrencyValue={currencyFormatter(this.state.currencyCode, this.state.languageCode)}
                    showCustomVendorCreditIDField={this.state.showCustomVendorCreditIDField}
                    updateAttachments={this.updateAttachments}
                    removePendingAttachment={this.removePendingAttachment}
                    isUploading={this.state.isUploading}
                    setIsUploading={this.setIsUploading}
                    pendingSessionId={this.state.pendingSessionId}
                    returnScroll={this.state.returnScroll}
                ></VendorCreditForm>
            }
            else if (VENDOR_FORM_MODES.includes(this.state.mode)) {
                return <VendorForm
                    mode={this.state.mode}
                    submitting={this.state[SUBMITTING_NAMES_BY_MODE[this.state.mode]]}
                    vendor={this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]]}
                    errors={this.state.errors[ERROR_NAMES_BY_MODE[this.state.mode]]}
                    onFormDataChange={(fieldName, fieldValue) => this.updateFormData(FORM_DATA_NAMES_BY_MODE[this.state.mode], fieldName, fieldValue)}
                    requestAction={this.handleActionRequest}
                    switchToSecondaryForm={undefined}
                    returnScroll={0}
                />
            }
            else if (LINE_ITEM_FORM_MODES.includes(this.state.mode)) {
                return <LineItemForm
                    mode={this.state.mode}
                    submitting={this.state.submittingLineItem}
                    parent={this.state.vendorCreditData}
                    lineItem={this.state.lineItemData}
                    formatCurrencyValue={currencyFormatter(this.state.currencyCode, this.state.languageCode)}
                    currencySymbol={getCurrencySymbol(this.state.currencyCode, this.state.languageCode)}
                    errors={this.state.errors.lineItem}
                    onFormDataChange={(fieldName, fieldValue) => this.updateFormData("lineItemData", fieldName, fieldValue)}
                    requestAction={this.handleActionRequest}
                    switchToPrimaryForm={this.switchToPrimaryForm}
                    returnScroll={0}
                    objectName={"Vendor Credit"}
                    preferredTimezone={this.state.preferredTimezone}
                    isVendorCreditLineItem={true}
                    parentLineItems={this.getBillLineItemChoices()}
                    parentLineItemFieldName="bill_line_item"
                    parentLineItemLabel="Bill Line Item"
                ></LineItemForm>
            }
            else {
                return (
                    <div className="data-panel-container data-panel-container--with-margin">
                        <div className="data-panel" aria-label="Unknown Form Mode">
                            <div className="data-panel__form">
                                <p className="data-panel__form__caption">An unhandled form mode was supplied.</p>
                            </div>
                        </div>
                    </div>
                )
            }
        }
    }
}

export default VendorCreditCreateContainer;
